<template>
  <v-container>
    <v-form ref="form" v-model="valid" lazy-validation>
      <!-- Event Name Display -->
      <label>
        <strong>Event Name: </strong>
        {{ selectedEvent.EventName ? selectedEvent.EventName : "N/A" }}
      </label>

      <!-- Basic Fields -->
      <v-text-field
        :value="formData.StallholderName"
        label="StallHolder Name"
        :rules="stallHolderNameRules"
        required
        @input="updateField('StallholderName', $event)"
      />

      <v-row class="mb-2">
        <v-col cols="6">
          <v-text-field
            :value="formData.Abn"
            label="Abn"
            :rules="abnRules"
            required
            @input="updateField('Abn', $event)"
          />
        </v-col>
        <v-col cols="5" offset-md="1" style="margin-top: 45px; font-size: 18px">
          <span>Use event settings</span>
        </v-col>
      </v-row>

      <!-- Payment Profile -->
      <v-row class="mb-2">
        <v-col cols="6">
          <label>Payment Profile</label>
          <v-row class="ml-1">
            <v-checkbox
              v-model="isCashSelected"
              label="Cash"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPaymentProfile"
            />
            <v-checkbox
              v-model="isCardSelected"
              label="Card"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPaymentProfile"
            />
            <v-checkbox
              v-model="isVoucherSelected"
              label="Voucher"
              :disabled="inheritanceStates.isSelectPaymentProfile"
            />
          </v-row>
        </v-col>
        <v-col cols="5" offset-md="1">
          <v-checkbox
            :input-value="inheritanceStates.isSelectPaymentProfile"
            :label="eventSettings.PaymentProfile"
            class="mr-5"
            style="padding-top: 12px"
            @change="
              handleInheritanceChange(
                'PaymentProfile',
                'PaymentProfileInherit',
                $event
              )
            "
          />
        </v-col>
      </v-row>

      <!-- Printing Profile -->
      <label>Printing Profile</label>
      <v-row class="ml-1">
        <v-col cols="6">
          <v-row>
            <v-checkbox
              v-model="isPrintSelected"
              label="Print"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPrintingProfile"
            />
            <v-checkbox
              v-model="isPromptSelected"
              label="Prompt"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPrintingProfile"
            />
            <v-checkbox
              v-model="isBypassSelected"
              label="Bypass"
              class="mr-5"
              :disabled="inheritanceStates.isSelectPrintingProfile"
            />
          </v-row>
          <!-- Hidden validation field for printing profile -->
          <v-text-field
            :value="formData.PrintingProfile"
            :rules="eventPrintRules"
            style="display: none"
            required
          />
        </v-col>
        <v-col cols="5" offset-md="1">
          <v-checkbox
            :input-value="inheritanceStates.isSelectPrintingProfile"
            :label="eventSettings.PrintingProfile"
            class="mr-5"
            style="margin-top: 4px; margin-left: -8px"
            @change="
              handleInheritanceChange(
                'PrintingProfile',
                'PrintingProfileInherit',
                $event
              )
            "
          />
        </v-col>
      </v-row>

      <!-- Drink Limit Profile -->
      <inheritable-field
        :value="standardDrinkLimitEnforceDisplayValue"
        :event-value="
          getEnforceTypeName(eventSettings.StandardDrinkLimitEnforce)
        "
        :inherit="inheritanceStates.isSelectStandardDrinkLimitEnforce"
        :event-setting-label="
          getEnforceTypeName(eventSettings.StandardDrinkLimitEnforce)
        "
        @input="standardDrinkLimitEnforceDisplayValue = $event"
        @inherit-change="
          handleInheritanceChange(
            'StandardDrinkLimitEnforce',
            'StandardDrinkLimitEnforceInherit',
            $event
          )
        "
      >
        <template #field="{ disabled, value, updateValue }">
          <v-combobox
            :value="value"
            :items="enforceTypes"
            item-text="name"
            item-value="name"
            label="Drink Limit Profile"
            :return-object="false"
            required
            :disabled="disabled"
            @input="updateValue"
          />
        </template>
      </inheritable-field>

      <!-- Maximum Drink Limit -->
      <inheritable-field
        :value="formData.StandardDrinkLimit"
        :event-value="eventSettings.StandardDrinkLimit"
        :inherit="inheritanceStates.isSelectStandardDrinkLimit"
        :event-setting-label="
          eventSettings.StandardDrinkLimit
            ? eventSettings.StandardDrinkLimit.toString()
            : ''
        "
        label="Maximum Drink Limit"
        type="number"
        min="0"
        required
        @input="updateField('StandardDrinkLimit', $event)"
        @inherit-change="
          handleInheritanceChange(
            'StandardDrinkLimit',
            'StandardDrinkLimitInherit',
            $event
          )
        "
      />

      <!-- Card Surcharge Percentage -->
      <inheritable-field
        :value="formData.CardSurchargePercentage"
        :event-value="eventSettings.CardSurchargePercentage"
        :inherit="inheritanceStates.isSelectCardSurchargePercentage"
        :event-setting-label="
          eventSettings.CardSurchargePercentage
            ? eventSettings.CardSurchargePercentage.toString()
            : ''
        "
        label="Card Surcharge Percentage"
        type="number"
        min="0"
        required
        @input="updateField('CardSurchargePercentage', $event)"
        @inherit-change="
          handleInheritanceChange(
            'CardSurchargePercentage',
            'CardSurchargePercentageInherit',
            $event
          )
        "
      />
    </v-form>
  </v-container>
</template>

<script>
import InheritableField from "./InheritableField.vue";

export default {
  name: "StallholderForm",
  components: {
    InheritableField,
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    eventSettings: {
      type: Object,
      required: true,
    },
    inheritanceStates: {
      type: Object,
      required: true,
    },
    selectedEvent: {
      type: Object,
      required: true,
    },
    printingList: {
      type: Array,
      required: true,
    },
    enforceTypes: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      valid: true,
      stallHolderNameRules: [(v) => !!v || "Stallholder Name is required"],
      abnRules: [(v) => !!v || "Abn is required"],
      eventPrintRules: [(v) => !!v || "Printing Profile is required"],
    };
  },
  computed: {
    isCashSelected: {
      get() {
        const result = (this.formData.PaymentProfile || []).includes("Cash");
        console.log(
          "isCashSelected getter:",
          result,
          "PaymentProfile:",
          this.formData.PaymentProfile
        );
        return result;
      },
      set(value) {
        console.log("isCashSelected setter:", value);
        this.updatePaymentProfile("Cash", value);
      },
    },
    isCardSelected: {
      get() {
        const result = (this.formData.PaymentProfile || []).includes("Card");
        console.log(
          "isCardSelected getter:",
          result,
          "PaymentProfile:",
          this.formData.PaymentProfile
        );
        return result;
      },
      set(value) {
        console.log("isCardSelected setter:", value);
        this.updatePaymentProfile("Card", value);
      },
    },
    isVoucherSelected: {
      get() {
        const result = (this.formData.PaymentProfile || []).includes("Voucher");
        console.log(
          "isVoucherSelected getter:",
          result,
          "PaymentProfile:",
          this.formData.PaymentProfile
        );
        return result;
      },
      set(value) {
        console.log("isVoucherSelected setter:", value);
        this.updatePaymentProfile("Voucher", value);
      },
    },
    isPrintSelected: {
      get() {
        const result = this.formData.PrintingProfile === "Print";
        console.log(
          "isPrintSelected getter:",
          result,
          "PrintingProfile:",
          this.formData.PrintingProfile
        );
        return result;
      },
      set(value) {
        console.log("isPrintSelected setter:", value);
        if (value) {
          // Only set when checking, ignore when unchecking
          this.updateField("PrintingProfile", "Print");
        }
      },
    },
    isPromptSelected: {
      get() {
        const result = this.formData.PrintingProfile === "Prompt";
        console.log(
          "isPromptSelected getter:",
          result,
          "PrintingProfile:",
          this.formData.PrintingProfile
        );
        return result;
      },
      set(value) {
        console.log("isPromptSelected setter:", value);
        if (value) {
          // Only set when checking, ignore when unchecking
          this.updateField("PrintingProfile", "Prompt");
        }
      },
    },
    isBypassSelected: {
      get() {
        const result = this.formData.PrintingProfile === "Bypass";
        console.log(
          "isBypassSelected getter:",
          result,
          "PrintingProfile:",
          this.formData.PrintingProfile
        );
        return result;
      },
      set(value) {
        console.log("isBypassSelected setter:", value);
        if (value) {
          // Only set when checking, ignore when unchecking
          this.updateField("PrintingProfile", "Bypass");
        }
      },
    },
    standardDrinkLimitEnforceDisplayValue: {
      get() {
        // Convert boolean to name for display
        let boolValue = this.formData.StandardDrinkLimitEnforce;

        // Handle string to boolean conversion if needed
        if (typeof boolValue === "string") {
          if (boolValue === "true" || boolValue === "Enforce") {
            boolValue = true;
          } else if (boolValue === "false" || boolValue === "Prompt") {
            boolValue = false;
          }
        }

        const enforceType = this.enforceTypes.find(
          (type) => type.id === boolValue
        );
        const result = enforceType ? enforceType.name : "";
        console.log(
          "standardDrinkLimitEnforceDisplayValue getter:",
          result,
          "from value:",
          this.formData.StandardDrinkLimitEnforce,
          "converted to boolean:",
          boolValue,
          "type:",
          typeof boolValue,
          "enforceTypes:",
          this.enforceTypes
        );
        return result;
      },
      set(nameValue) {
        // Convert name back to boolean for storage
        console.log("standardDrinkLimitEnforceDisplayValue setter:", nameValue);
        const enforceType = this.enforceTypes.find(
          (type) => type.name === nameValue
        );
        const boolValue = enforceType ? enforceType.id : false;
        console.log("Converting name to boolean:", nameValue, "->", boolValue);
        this.updateField("StandardDrinkLimitEnforce", boolValue);
      },
    },
  },
  watch: {
    formData: {
      handler(newVal) {
        console.log("StallholderForm formData changed:", newVal);
        if (newVal.PaymentProfile) {
          console.log(
            "PaymentProfile:",
            newVal.PaymentProfile,
            "Type:",
            typeof newVal.PaymentProfile
          );
        }
      },
      deep: true,
    },
  },
  methods: {
    updateField(field, value) {
      this.$emit("update-field", field, value);
    },
    getEnforceTypeName(boolValue) {
      const enforceType = this.enforceTypes.find(
        (type) => type.id === boolValue
      );
      return enforceType ? enforceType.name : "";
    },
    handleInheritanceChange(fieldKey, inheritKey, useEventSetting) {
      this.$emit("inheritance-change", fieldKey, inheritKey, useEventSetting);
    },
    updatePaymentProfile(value, checked) {
      let currentProfile = [...(this.formData.PaymentProfile || [])];
      if (checked) {
        if (!currentProfile.includes(value)) {
          currentProfile.push(value);
        }
      } else {
        currentProfile = currentProfile.filter((item) => item !== value);
      }
      this.updateField("PaymentProfile", currentProfile);
    },
    updatePrintingProfile(value, checked) {
      let currentProfile = this.formData.PrintingProfile || "";
      if (checked) {
        currentProfile = value;
      } else {
        currentProfile = "";
      }
      this.updateField("PrintingProfile", currentProfile);
    },
    validate() {
      return this.$refs.form.validate();
    },
  },
};
</script>
